import { Button,Skeleton,Typography } from "@mui/material";
import { createTheme,ThemeProvider } from '@mui/material/styles';
import { Stack } from "@mui/system";
import { useMutation,useQueries } from "@tanstack/react-query";
import MUIDataTable,{ MUIDataTableOptions } from "mui-datatables";
import { useEffect,useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import AttToken from "../../helpers/attToken";
import { trackEventMatomo,trackEventMatomoVisit } from "../../helpers/matomo";
import Checklists from "../../service/Checklists";
import Clients from "../../service/Clients";
import Manages from "../../service/Manages";
import queryClient from "../../service/query";
import Teams from "../../service/Teams";
import Users from "../../service/Users";
import { gerenciaType } from "../../types/managements/types";
import { tableOptions } from "../TableHelpers/options";
import ChangeManageModal from "./Modal/ChangeManageModal";

const managesService = new Manages();
const customerService = new Clients();
const checklistService = new Checklists();
const teamsService = new Teams();
const usersService = new Users();

export default function ManagesLegacy() {
  const { t } = useTranslation('translation');
  const [open, setOpen] = useState(false);
  const [selectedManage, setSelectedManage] = useState<gerenciaType | null>();

  useEffect(() => {
    trackEventMatomoVisit('Gestão de gerencias');
  }, []);

  const result = useQueries({
    queries: [
      {
        queryKey: ['GET_MANAGES'],
        queryFn: async () => {
          const token = await AttToken();
          if (token) {
            const response = await managesService.getAllManages(token);
            return response;
          }
        },
        retry: 2,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        staleTime: 5 * 60 * 1000,
      },
      {
        queryKey: ['GET_CUSTOMERS'],
        queryFn: async () => {
          const token = await AttToken();
          if (token) {
            const response = await customerService.getAllClients(token, true);
            return response?.clientList;
          }
        },
        retry: 2,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        staleTime: 5 * 60 * 1000,
      },
      {
        queryKey: ['GET_TEAMS'],
        queryFn: async () => {
          const token = await AttToken();
          if (token) {
            const response = await teamsService.getAllByCurrentUser(token);
            return response;
          }
        },
        retry: 2,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        staleTime: 5 * 60 * 1000,
      },
      {
        queryKey: ['GET_USERS'],
        queryFn: async () => {
          const token = await AttToken();
          if (token) {
            const response = await usersService.getAllUsers(token);
            return response?.users;
          }
        },
        retry: 2,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        staleTime: 5 * 60 * 1000,
      },
      {
        queryKey: ['GET_FORMS'],
        queryFn: async () => {
          const token = await AttToken();
          if (token) {
            const body = { ...token };
            const response = await checklistService.getSimpleForm(body);
            return response;
          }
        },
        retry: 2,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        staleTime: 5 * 60 * 1000,
      }
    ]
  });

  const isLoading = result?.some((result) => result.isLoading);
  const isFetching = result?.some((result) => result.isFetching);
  const isSuccess = result?.every((query) => query.isSuccess);
  const isError = result?.some((query) => query.isError);

  useEffect(() => {
    if (isSuccess) trackEventMatomo('gestão de gerências', 'sucesso', 'carregamento', 'dados das gerências carregados');
    if (isError) trackEventMatomo('gestão de gerências', 'erro', 'carregamento', 'falha ao carregar dados das gerências');
  }, [isError, isSuccess]);

  const { mutate, isPending } = useMutation({
    mutationKey: ['NEW_MANAGE1'],
    mutationFn: async () => {
      const promise = toast.promise(async () => {
        const token = await AttToken();
        if (token) {
          const body = { ...token, nome: `${t('Managements.newManage')}` };
          const response = await managesService.createManage(body);
          if (!response) {
            throw new Error('Error creating manage');
          }
          return response;
        }
      }, {
        success: `${t('Managements.toastSuccess')}`,
        error: `${t('custom.toastError')}`,
      }, { toastId: 'create-manage' });
      return promise;
    },
    onSuccess: () => {
      trackEventMatomo('gestão de gerências', 'sucesso', 'criação', 'gerência criada');
      queryClient.resetQueries({ queryKey: ['GET_MANAGES'] });
    },
    onError: () => trackEventMatomo('gestão de gerências', 'erro', 'criação', 'falha ao criar gerência'),
  });

  const loading = isPending || isLoading || isFetching;

  const columns = [
    {
      name: "nome",
      label: t('Managements.manage'),
      options: {
        filter: false,
        sort: true,
        sortThirdClickReset: true,
        customBodyRender: (value: string) => (<Typography fontSize='small' sx={{ wordBreak: 'break-word' }}>{value}</Typography>)
      }
    },
    {
      name: 'dataCriacao',
      label: t('Managements.createdAt'),
      options: {
        filter: false,
        sort: true,
        sortThirdClickReset: true,
        customBodyRender: (value: number) => {
          const data = new Date(value).toLocaleString("pt-br");
          return data;
        },
      }
    },
    {
      name: 'dataDelecao',
      label: t('status'),
      options: {
        customBodyRender: (value: number) => {
          return <>
            {value && parseInt(value as unknown as string) !== 1
              ? <Typography color='error' fontWeight={700}>{`${t('Managements.unactiveAt')} ${new Date(parseInt(value as unknown as string)).toLocaleString('pt-BR')}`}</Typography>
              : <Typography sx={{ color: '#00C4A7' }} fontWeight={700}>{t('active')}</Typography>
            }
          </>;

        },
        filter: false,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: "id",
      label: t('edit'),
      options: {
        display: false,
        filter: false,
        sort: false,
      }
    },
  ];

  const translatedTextLabels: MUIDataTableOptions = {
    onRowClick: (rowData) => {
      trackEventMatomo('gestão de gerências', 'click', 'linha', 'edita gerência');
      setOpen(true);
      const manage = [...new Set(result?.[0]?.data as gerenciaType[])]?.filter((el) => el.id === rowData[3])[0];
      if (manage)
        setSelectedManage(manage);
    },
    textLabels: {
      body: {
        noMatch: `${t('table.noMatch')}`,
        toolTip: `${t('table.toolTip')}`,
        columnHeaderTooltip: column =>
          `${t('table.columnHeaderTooltip')} ${column.label}`
      },
      pagination: {
        next: `${t('table.next')}`,
        previous: `${t('table.previous')}`,
        rowsPerPage: `${t('table.rowsPerPage')}`,
        displayRows: `${t('table.displayRows')}`
      },
      toolbar: {
        search: `${t('table.search')}`,
        filterTable: `${t('table.filterTable')}`
      },
      filter: {
        title: `${t('table.title')}`,
        reset: `${t('table.reset')}`
      },
      viewColumns: {
        title: `${t('table.viewColumnsTitle')}`
      },
    },
  };

  if (result.some((ele) => ele.isError)) {
    return <span>{t('notifications.unread.errorLoading')}</span>;
  }

  const getMuiTheme = () => createTheme({
    components: {
      MuiPaper: {
        styleOverrides: {
          root: {
            width: '100%',
            minWidth: '100%',
          }
        }
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            textAlign: 'initial',
            padding: '10px'
          }
        }
      }
    }
  });

  return (
    <Stack width='100%' direction="column" justifyContent='flex-start' alignItems='flex-start' spacing={1.5}>
      {open &&
        <ChangeManageModal
          open={open}
          setOpen={setOpen}
          result={result}
          selectedManage={selectedManage as gerenciaType}
          setSelectedManage={setSelectedManage}
        />}
      <Button
        size='large'
        color='success'
        variant="contained"
        disabled={loading}
        onClick={() => {
          trackEventMatomo('gestão de gerências', 'click', 'botão', 'cria gerência');
          mutate();
        }}
      >
        {t('Managements.addManageBtn')}
      </Button>
      {loading ?
        <Skeleton variant="rounded" width='100%' height={600} />
        : <ThemeProvider theme={getMuiTheme()}>
          <MUIDataTable
            title={t('Managements.tabtitle')}
            data={[...new Set(result?.[0]?.data as gerenciaType[])]}
            columns={columns}
            options={{ ...tableOptions, ...translatedTextLabels }}
          />
        </ThemeProvider>}
    </Stack>
  );
}
